import { create } from "zustand";
import { useEffect, useMemo } from "react";

import { getJSON, patchJSON, postJSON } from "../lib/api";
import { adaptTodayQueue } from "../lib/adapt";
import { FIXTURE_CLIENTS, FIXTURE_CONTACTS } from "../lib/fixtures";
import { IS_PREVIEW } from "../lib/pro-mode";
import { previewTodayQueueFor } from "../lib/pro-home-preview";
import { useProToken, type GetProToken } from "../hooks/useProToken";
import { loadRequestRows, useMarketplaceRequestListStore } from "./marketplaceRequestList";
import { useMarketplaceRequestsRefreshStore } from "./marketplaceRequests";
import type { TodayQueueResponse, TodayGroupKey } from "../lib/types";
import type {
  TodayGroupVM,
  TodayQueueVM,
  TodaySignalVM,
  TodayTaskItemVM,
  TodayTaskVM,
  SillSuggestion,
} from "../lib/view-model";

type TodayMutationKind = "done" | "dismissed" | "snoozed";
type TaskMutationKind = "done";

type SignalState =
  | { state: "pending"; action: TodayMutationKind }
  | { state: "archived"; action: TodayMutationKind }
  | { state: "error"; action: TodayMutationKind; error: string };

type TaskState =
  | { state: "pending"; action: TaskMutationKind }
  | { state: "archived"; action: TaskMutationKind }
  | { state: "error"; action: TaskMutationKind; error: string };

type PendingTaskCreate = {
  tempKey: string;
  item: TodayTaskItemVM;
};

interface TodayStoreState {
  data: TodayQueueVM | null;
  isLoading: boolean;
  isRefreshing: boolean;
  error: string | null;
  hasLoaded: boolean;
  notice: string | null;
  selectedGroupKey: TodayGroupKey | null;
  zeroedGroupKeys: TodayGroupKey[];
  signalStates: Record<string, SignalState>;
  taskStates: Record<string, TaskState>;
  pendingTaskCreates: Record<string, PendingTaskCreate>;

  ensureLoaded: (getToken: GetProToken) => Promise<void>;
  refresh: (
    getToken: GetProToken,
    opts?: { ignoreRemovedKeys?: string[] },
  ) => Promise<void>;
  reset: () => void;
  selectGroupKey: (key: TodayGroupKey | null) => void;
  clearNotice: () => void;
  beginSignalMutation: (
    itemKey: string,
    action: TodayMutationKind,
  ) => TodaySignalVM | null;
  markSignalSuccess: (itemKey: string) => void;
  markSignalError: (
    itemKey: string,
    action: TodayMutationKind,
    error: string,
  ) => void;
  undoSignal: (itemKey: string) => void;
  beginTaskDoneMutation: (itemKey: string) => TodayTaskItemVM | null;
  markTaskDoneSuccess: (itemKey: string) => void;
  markTaskDoneError: (itemKey: string, error: string) => void;
  startTaskCreate: (tempKey: string, item: TodayTaskItemVM) => void;
  finishTaskCreate: (tempKey: string, task?: TodayTaskVM) => void;
  addTodayTask: (task: TodayTaskVM) => void;
  failTaskCreate: (tempKey: string, error: string) => void;
}

const TODAY_GROUP_ORDER: TodayGroupKey[] = [
  "new",
  "readiness_moved",
  "high_intent",
  "timing",
  "your_followups",
];

let inFlight: Promise<void> | null = null;
let requestsInFlight: Promise<void> | null = null;
// The token resolver of the last load, so a requests-store bump can refetch
// the requests summary without a component handing the store a new one.
let lastGetToken: GetProToken | null = null;
// Whether the Today queue may read the marketplace requests list. The queue is
// CRM data; the requests summary merged onto it is marketplace data and must
// not be requested while the go-live gate is `off` or still `loading`. Fail
// closed: nothing reads it until a gate-aware consumer (`useHomeNeeds`, mounted
// by the header on every route) reports the gate `on`.
let marketplaceReads = false;

/**
 * Tell the store whether the marketplace go-live gate is `on`. Turning it on
 * after Today loaded fetches the requests summary then; turning it off stops
 * further reads (the summary already on the queue is left as it was).
 */
export function setTodayMarketplaceReads(on: boolean): void {
  if (marketplaceReads === on) return;
  marketplaceReads = on;
  if (on) void refreshMarketplaceRequestsSummary();
}

// Today item mutations (done / snooze / dismiss / add) are announced here so
// `stores/proHome.ts` can invalidate without today.ts importing it.
const mutationListeners = new Set<() => void>();

/** Subscribe to a Today item mutation having landed. Returns the unsubscribe. */
export function onTodayMutation(listener: () => void): () => void {
  mutationListeners.add(listener);
  return () => {
    mutationListeners.delete(listener);
  };
}

function notifyTodayMutation(): void {
  for (const listener of [...mutationListeners]) listener();
}

function cloneQueue(queue: TodayQueueVM): TodayQueueVM {
  return {
    groups: queue.groups.map((group) => ({
      ...group,
      items: group.items.map((item) => ({ ...item })),
    })),
    snoozed: queue.snoozed.map((item) => ({ ...item })),
    drafts: (queue.drafts ?? []).map((d) => ({ ...d })),
    marketplaceRequests: queue.marketplaceRequests
      ? { ...queue.marketplaceRequests }
      : undefined,
    openCount: queue.openCount,
    linkedCount: queue.linkedCount,
    truncated: queue.truncated,
  };
}

function fixtureContactLabel(index = 0): {
  id: string;
  name: string;
  email?: string;
} {
  const contact = FIXTURE_CONTACTS[index] ?? FIXTURE_CONTACTS[0];
  if (!contact) {
    return { id: "contact-preview", name: "Taylor Buyer" };
  }
  return {
    id: contact.id,
    name: contact.name,
    email: contact.email,
  };
}

function previewTask(
  id: string,
  title: string,
  dueLabel: string,
  contact?: { id: string; name: string; email?: string },
): TodayTaskItemVM {
  return itemForTodayTask({
    id,
    title,
    dueLabel,
    dueAt: undefined,
    createdAt: new Date().toISOString(),
    contact,
  });
}

function itemForTodayTask(task: TodayTaskVM): TodayTaskItemVM {
  return {
    kind: "task",
    itemKey: `task:${task.id}`,
    group: "your_followups",
    task,
    contactLabel: task.contact?.name,
    dueLabel: task.dueLabel,
  };
}

function queueWithTodayTask(queue: TodayQueueVM, task: TodayTaskVM): TodayQueueVM {
  const item = itemForTodayTask(task);
  if (queue.groups.some((group) => group.items.some((entry) => entry.itemKey === item.itemKey))) {
    return queue;
  }

  const groups = queue.groups.map((group) =>
    group.key === "your_followups"
      ? { ...group, count: group.count + 1, items: [item, ...group.items] }
      : group,
  );
  if (!groups.some((group) => group.key === "your_followups")) {
    groups.push({ key: "your_followups", count: 1, items: [item] });
  }
  return {
    ...queue,
    groups,
    openCount: groups.reduce((sum, group) => sum + group.count, 0),
  };
}

function buildPreviewTodayQueue(): TodayQueueVM {
  const [bank, doc, self, timing] = FIXTURE_CLIENTS;
  const groups: TodayGroupVM[] = [
    {
      key: "new",
      count: 1,
      items: [
        {
          kind: "signal",
          itemKey: "event:preview-new",
          group: "new",
          client: bank,
          eventType: "invite_accepted",
          occurredAt: "2026-06-11T10:00:00Z",
          reason: "Accepted your invite",
          lastActivityAt: "2026-06-11T10:00:00Z",
          goingCold: false,
        },
      ],
    },
    {
      key: "readiness_moved",
      count: 1,
      items: [
        {
          kind: "signal",
          itemKey: "event:preview-readiness",
          group: "readiness_moved",
          client: doc ?? bank,
          eventType: "bank_connected",
          occurredAt: "2026-06-11T09:00:00Z",
          reason: "Connected a bank",
          lastActivityAt: "2026-06-10T18:00:00Z",
          goingCold: false,
        },
      ],
    },
    {
      key: "timing",
      count: 1,
      items: [
        {
          kind: "signal",
          itemKey: "event:preview-timing",
          group: "timing",
          client: timing ?? self,
          eventType: "lease_window_hit",
          occurredAt: "2026-06-11T08:00:00Z",
          reason: "Entered their lease window",
          lastActivityAt: "2026-05-30T18:00:00Z",
          goingCold: true,
        },
      ],
    },
    {
      key: "your_followups",
      count: 2,
      items: [
        previewTask(
          "task-preview-1",
          "Call lender",
          "Due tomorrow",
          fixtureContactLabel(0),
        ),
        previewTask("task-preview-2", "Send listing recap", "No due date"),
      ],
    },
  ];
  return {
    groups,
    snoozed: [],
    drafts: [],
    // Reachable in IS_PREVIEW so the Today-queue marketplace-requests row
    // (`MarketplaceRequestsSection`) has a deterministic fixture for
    // Playwright/ui-verify captures without a backend — mirrors the M9
    // build's own `TodayMarketplaceRequestsSummary` doc comment ("the field
    // exists so the shape is ready"); this is that readiness realized for
    // the preview fixture specifically (M9 remediation, finding 8).
    marketplaceRequests: { count: 1, topFeeCents: 15000 },
    openCount: groups.reduce((sum, group) => sum + group.count, 0),
    linkedCount: 3,
    truncated: false,
  };
}

function emptyQueue(): TodayQueueVM {
  return {
    groups: [],
    snoozed: [],
    drafts: [],
    openCount: 0,
    linkedCount: 0,
    truncated: false,
  };
}

function queueWithOpenCount(openCount: number): TodayQueueVM {
  if (openCount <= 0) return emptyQueue();
  return {
    groups: [
      {
        key: "new",
        count: openCount,
        items: Array.from({ length: openCount }, (_, index) => ({
          kind: "signal" as const,
          itemKey: `test:${index}`,
          group: "new" as const,
          client: {
            id: `test:${index}`,
            name: `Test Client ${index + 1}`,
            avatar: "TC",
            tier: 1 as const,
            timeline: "Buying in 3–6 months",
            timelineShort: "3–6 mo",
            lastActivity: "just now",
            minutesAgo: 0,
            changed: "",
            newEvents: 0,
            activeThisWeek: true,
            changedThisWeek: false,
            needsResponse: true,
            area: "Test area",
            goal: "Test goal",
            budget: "Not shared",
            creditBand: "Not shared",
            financials: {
              income: { verb: "Self-reported", date: null, kind: "self" },
              savings: { verb: "Self-reported", date: null, kind: "self" },
              debts: { verb: "Self-reported", date: null, kind: "self" },
            },
            underwriting: null,
            activity: [],
            affordability: {
              qualPrice: null,
              band: null,
              bandCompact: null,
              movement: null,
              downPayment: "pending",
              targetBudget: null,
              pending: true,
              score: null,
              segment: null,
              segmentLabel: null,
              target: null,
            },
            connection: {
              linkId: `test:${index}`,
              role: "agent",
              sharingLevel: "status_only",
              funnelState: "linked",
            },
          },
          eventType: "profile_updated",
          occurredAt: new Date().toISOString(),
          reason: "Test signal",
          lastActivityAt: new Date().toISOString(),
          goingCold: false,
        })),
      },
    ],
    snoozed: [],
    drafts: [],
    openCount,
    linkedCount: openCount > 0 ? openCount : 0,
    truncated: false,
  };
}

export function setTodayOpenCount(openCount: number) {
  const queue = queueWithOpenCount(openCount);
  useTodayStore.setState((state) => ({
    ...state,
    data: queue,
    hasLoaded: true,
    isLoading: false,
    isRefreshing: false,
    error: null,
    selectedGroupKey: queue.groups[0]?.key ?? null,
    zeroedGroupKeys: [],
  }));
}

// A pending-requests list loaded this recently (for the current bump version)
// feeds the queue's first load instead of a second request.
const REQUESTS_REUSE_MS = 10_000;

/**
 * The pending marketplace-connection-requests summary (count + top fee).
 * The `/api/professional/today` DTO does not carry this, so it is derived from
 * the list the Requests inbox and the header badge use
 * (`GET /api/marketplace/pro/requests`), read through the shared
 * `marketplaceRequestList` store so one request feeds all of them, and merged
 * onto the Today queue. `null` = the read failed.
 */
async function loadMarketplaceRequestsSummary(
  getToken: GetProToken,
  opts?: { maxAgeMs?: number },
): Promise<{ count: number; topFeeCents?: number } | null> {
  const rows = await loadRequestRows(getToken, opts);
  if (rows === null) return null;
  // An own-client request (backend `own_client`) is the pro's own buyer with
  // a $0 retention quote, not a lead with a fee: it counts, but it never
  // becomes the top fee, so a lone own-client request leaves the fee unset
  // and the Tasks row reads "Review request" rather than "· $0".
  const paid = rows.filter((row) => row.own_client !== true);
  const topFeeCents = paid.reduce(
    (max, row) => Math.max(max, row.fee_quote?.net_cents ?? 0),
    0,
  );
  return {
    count: rows.length,
    topFeeCents: paid.length > 0 ? topFeeCents : undefined,
  };
}

/**
 * Best-effort: a failure here must never break Today itself — it just leaves
 * the row absent (count 0).
 */
async function fetchMarketplaceRequestsSummary(
  getToken: GetProToken,
  opts?: { maxAgeMs?: number },
): Promise<{ count: number; topFeeCents?: number }> {
  return (await loadMarketplaceRequestsSummary(getToken, opts)) ?? { count: 0 };
}

function fetchTodayQueue(
  getToken: GetProToken,
  { reuseRequests = false }: { reuseRequests?: boolean } = {},
): Promise<TodayQueueVM> {
  if (IS_PREVIEW) {
    return previewTodayQueueFor(buildPreviewTodayQueue());
  }
  return (async () => {
    const token = (await getToken()) ?? undefined;
    const withMarketplace = marketplaceReads;
    const [res, marketplaceRequests] = await Promise.all([
      getJSON<TodayQueueResponse>("/api/professional/today", token),
      withMarketplace
        ? fetchMarketplaceRequestsSummary(
            getToken,
            reuseRequests ? { maxAgeMs: REQUESTS_REUSE_MS } : undefined,
          )
        : Promise.resolve(undefined),
    ]);
    return { ...adaptTodayQueue(res), marketplaceRequests };
  })();
}

/**
 * The gate turned `on` while a queue fetch that began without the requests
 * summary was in flight: fetch the summary now that the queue has landed.
 */
function backfillMarketplaceRequests(): void {
  if (marketplaceReads && !useTodayStore.getState().data?.marketplaceRequests) {
    void refreshMarketplaceRequestsSummary();
  }
}

function recomputeVisibleQueue(state: TodayStoreState): TodayQueueVM {
  if (!state.data) return emptyQueue();

  const queue = cloneQueue(state.data);
  const nextGroups: TodayGroupVM[] = [];
  const pendingTaskItems = Object.values(state.pendingTaskCreates).map(
    (entry) => entry.item,
  );

  for (const group of queue.groups) {
    const visibleItems = group.items.filter((item) => {
      if (item.kind === "signal") {
        const signalState = state.signalStates[item.itemKey];
        return (
          signalState?.state !== "pending" && signalState?.state !== "archived"
        );
      }
      const taskState = state.taskStates[item.itemKey];
      return taskState?.state !== "pending" && taskState?.state !== "archived";
    });

    const pendingInGroup =
      group.key === "your_followups" ? pendingTaskItems : [];
    const items = [...pendingInGroup, ...visibleItems];
    if (items.length === 0) continue;
    nextGroups.push({ ...group, count: items.length, items });
  }

  const openCount = nextGroups.reduce((sum, group) => sum + group.count, 0);
  return {
    ...queue,
    groups: nextGroups,
    openCount,
  };
}

function refreshNoticeForRemoval(
  prevVisible: TodayQueueVM,
  nextVisible: TodayQueueVM,
): string | null {
  const nextKeys = new Set(
    nextVisible.groups.flatMap((group) =>
      group.items.map((item) => item.itemKey),
    ),
  );
  const removedSignals = prevVisible.groups.flatMap((group) =>
    group.items.filter(
      (item): item is TodaySignalVM =>
        item.kind === "signal" && !nextKeys.has(item.itemKey),
    ),
  );
  if (removedSignals.length === 0) {
    const removedCount = prevVisible.groups
      .flatMap((group) => group.items)
      .filter((item) => !nextKeys.has(item.itemKey)).length;
    if (removedCount === 0) return null;
    return `${removedCount} item${removedCount === 1 ? "" : "s"} disappeared after refresh.`;
  }

  const names = removedSignals.map((item) => item.client.name).filter(Boolean);
  const firstName = names[0] ?? "A buyer";
  if (removedSignals.length === 1) {
    return `${firstName} unlinked — removed from Today.`;
  }
  if (removedSignals.length === 2) {
    return `${firstName} and ${names[1] ?? "another buyer"} unlinked — removed from Today.`;
  }
  return `${firstName} and ${removedSignals.length - 1} others unlinked — removed from Today.`;
}

function groupCounts(queue: TodayQueueVM): Map<TodayGroupKey, number> {
  return new Map(queue.groups.map((group) => [group.key, group.count]));
}

function reconcileZeroed(
  prevQueue: TodayQueueVM,
  nextQueue: TodayQueueVM,
  prevZeroed: TodayGroupKey[],
): TodayGroupKey[] {
  const prevCounts = groupCounts(prevQueue);
  const nextCounts = groupCounts(nextQueue);
  const zeroed = new Set(prevZeroed);
  for (const key of TODAY_GROUP_ORDER) {
    const before = prevCounts.get(key) ?? 0;
    const after = nextCounts.get(key) ?? 0;
    if (before > 0 && after === 0) zeroed.add(key);
    if (after > 0) zeroed.delete(key);
  }
  return Array.from(zeroed);
}

function selectedGroupFor(
  queue: TodayQueueVM,
  current: TodayGroupKey | null,
): TodayGroupKey | null {
  if (current && queue.groups.some((group) => group.key === current)) {
    return current;
  }
  return queue.groups[0]?.key ?? null;
}

function findSignal(
  queue: TodayQueueVM,
  itemKey: string,
): TodaySignalVM | null {
  for (const group of queue.groups) {
    for (const item of group.items) {
      if (item.kind === "signal" && item.itemKey === itemKey) return item;
    }
  }
  return null;
}

function findTask(queue: TodayQueueVM, itemKey: string): TodayTaskItemVM | null {
  for (const group of queue.groups) {
    for (const item of group.items) {
      if (item.kind === "task" && item.itemKey === itemKey) return item;
    }
  }
  return null;
}

function initialState(): Pick<
  TodayStoreState,
  | "data"
  | "isLoading"
  | "isRefreshing"
  | "error"
  | "hasLoaded"
  | "notice"
  | "selectedGroupKey"
  | "zeroedGroupKeys"
  | "signalStates"
  | "taskStates"
  | "pendingTaskCreates"
> {
  return {
    data: null,
    isLoading: true,
    isRefreshing: false,
    error: null,
    hasLoaded: false,
    notice: null,
    selectedGroupKey: null,
    zeroedGroupKeys: [],
    signalStates: {},
    taskStates: {},
    pendingTaskCreates: {},
  };
}

export const useTodayStore = create<TodayStoreState>((set, get) => ({
  ...initialState(),

  ensureLoaded: async (getToken) => {
    lastGetToken = getToken;
    if (get().hasLoaded) return;
    if (inFlight) return inFlight;
    inFlight = (async () => {
      set({ isLoading: true, error: null });
      try {
        const next = await fetchTodayQueue(getToken, { reuseRequests: true });
        set((state) => {
          const prevVisible = state.data
            ? recomputeVisibleQueue(state)
            : emptyQueue();
          const nextState = { ...state, data: next } as TodayStoreState;
          const nextVisible = recomputeVisibleQueue(nextState);
          return {
            data: next,
            isLoading: false,
            isRefreshing: false,
            error: null,
            hasLoaded: true,
            selectedGroupKey: selectedGroupFor(
              nextVisible,
              state.selectedGroupKey,
            ),
            zeroedGroupKeys: reconcileZeroed(
              prevVisible,
              nextVisible,
              state.zeroedGroupKeys,
            ),
            notice: state.notice,
          };
        });
      } catch (err) {
        set((state) => ({
          ...state,
          isLoading: false,
          isRefreshing: false,
          error: err instanceof Error ? err.message : "Failed to load Today",
        }));
      }
    })().finally(() => {
      inFlight = null;
      backfillMarketplaceRequests();
    });
    return inFlight;
  },

  refresh: async (getToken, opts) => {
    lastGetToken = getToken;
    if (inFlight) return inFlight;
    inFlight = (async () => {
      const prev = get();
      const prevVisible = prev.data
        ? recomputeVisibleQueue(prev)
        : emptyQueue();
      set({ isRefreshing: true, error: null });
      try {
        const next = await fetchTodayQueue(getToken);
        const nextVisible = recomputeVisibleQueue({
          ...prev,
          data: next,
        } as TodayStoreState);
        set((state) => {
          const notice = opts?.ignoreRemovedKeys?.length
            ? state.notice
            : (refreshNoticeForRemoval(prevVisible, nextVisible) ??
              state.notice);
          return {
            data: next,
            isLoading: false,
            isRefreshing: false,
            error: null,
            hasLoaded: true,
            selectedGroupKey: selectedGroupFor(
              nextVisible,
              state.selectedGroupKey,
            ),
            zeroedGroupKeys: reconcileZeroed(
              prevVisible,
              nextVisible,
              state.zeroedGroupKeys,
            ),
            notice,
          };
        });
      } catch (err) {
        set((state) => ({
          ...state,
          isLoading: false,
          isRefreshing: false,
          error: err instanceof Error ? err.message : "Failed to refresh Today",
        }));
      }
      void prev;
    })().finally(() => {
      inFlight = null;
      backfillMarketplaceRequests();
    });
    return inFlight;
  },

  reset: () => {
    inFlight = null;
    requestsInFlight = null;
    lastGetToken = null;
    marketplaceReads = false;
    useMarketplaceRequestListStore.getState().reset();
    set(initialState());
  },

  selectGroupKey: (key) => set({ selectedGroupKey: key }),
  clearNotice: () => set({ notice: null }),

  beginSignalMutation: (itemKey, action) => {
    const current = get();
    const signal = current.data
      ? findSignal(recomputeVisibleQueue(current), itemKey)
      : null;
    if (!signal) return null;
    set((state) => ({
      signalStates: {
        ...state.signalStates,
        [itemKey]: { state: "pending", action },
      },
      notice: null,
    }));
    const nextVisible = recomputeVisibleQueue({
      ...current,
      signalStates: {
        ...current.signalStates,
        [itemKey]: { state: "pending", action },
      },
    } as TodayStoreState);
    set((state) => ({
      zeroedGroupKeys: reconcileZeroed(
        recomputeVisibleQueue(state),
        nextVisible,
        state.zeroedGroupKeys,
      ),
      selectedGroupKey: selectedGroupFor(nextVisible, state.selectedGroupKey),
    }));
    return signal;
  },

  markSignalSuccess: (itemKey) => {
    const current = get();
    const signalState = current.signalStates[itemKey];
    if (!signalState) return;
    const nextState: SignalState = {
      state: "archived",
      action: signalState.action,
    };
    const nextStore = {
      ...current,
      signalStates: {
        ...current.signalStates,
        [itemKey]: nextState,
      },
    } as TodayStoreState;
    const prevVisible = recomputeVisibleQueue(current);
    const nextVisible = recomputeVisibleQueue(nextStore);
    set({
      signalStates: nextStore.signalStates,
      zeroedGroupKeys: reconcileZeroed(
        prevVisible,
        nextVisible,
        current.zeroedGroupKeys,
      ),
      selectedGroupKey: selectedGroupFor(nextVisible, current.selectedGroupKey),
    });
    notifyTodayMutation();
  },

  markSignalError: (itemKey, action, error) => {
    const current = get();
    const nextSignalStates = { ...current.signalStates };
    nextSignalStates[itemKey] = { state: "error", action, error };
    const nextStore = {
      ...current,
      signalStates: nextSignalStates,
    } as TodayStoreState;
    const prevVisible = recomputeVisibleQueue(current);
    const nextVisible = recomputeVisibleQueue(nextStore);
    set({
      signalStates: nextSignalStates,
      zeroedGroupKeys: reconcileZeroed(
        prevVisible,
        nextVisible,
        current.zeroedGroupKeys,
      ),
      selectedGroupKey: selectedGroupFor(nextVisible, current.selectedGroupKey),
    });
  },

  undoSignal: (itemKey) => {
    const current = get();
    const signalState = current.signalStates[itemKey];
    if (!signalState || signalState.state !== "archived") return;
    const nextSignalStates = { ...current.signalStates };
    delete nextSignalStates[itemKey];
    const nextStore = {
      ...current,
      signalStates: nextSignalStates,
    } as TodayStoreState;
    const prevVisible = recomputeVisibleQueue(current);
    const nextVisible = recomputeVisibleQueue(nextStore);
    set({
      signalStates: nextSignalStates,
      zeroedGroupKeys: reconcileZeroed(
        prevVisible,
        nextVisible,
        current.zeroedGroupKeys,
      ),
      selectedGroupKey: selectedGroupFor(nextVisible, current.selectedGroupKey),
    });
  },

  beginTaskDoneMutation: (itemKey) => {
    const current = get();
    const task = current.data
      ? findTask(recomputeVisibleQueue(current), itemKey)
      : null;
    if (!task) return null;
    const nextTaskStates = {
      ...current.taskStates,
      [itemKey]: { state: "pending", action: "done" } satisfies TaskState,
    };
    const nextStore = {
      ...current,
      taskStates: nextTaskStates,
    } as TodayStoreState;
    const prevVisible = recomputeVisibleQueue(current);
    const nextVisible = recomputeVisibleQueue(nextStore);
    set({
      taskStates: nextTaskStates,
      zeroedGroupKeys: reconcileZeroed(
        prevVisible,
        nextVisible,
        current.zeroedGroupKeys,
      ),
      selectedGroupKey: selectedGroupFor(nextVisible, current.selectedGroupKey),
      notice: null,
    });
    return task;
  },

  markTaskDoneSuccess: (itemKey) => {
    const current = get();
    const taskState = current.taskStates[itemKey];
    if (!taskState) return;
    const nextTaskStates = {
      ...current.taskStates,
      [itemKey]: { state: "archived", action: "done" } satisfies TaskState,
    };
    const nextStore = {
      ...current,
      taskStates: nextTaskStates,
    } as TodayStoreState;
    const prevVisible = recomputeVisibleQueue(current);
    const nextVisible = recomputeVisibleQueue(nextStore);
    set({
      taskStates: nextTaskStates,
      zeroedGroupKeys: reconcileZeroed(
        prevVisible,
        nextVisible,
        current.zeroedGroupKeys,
      ),
      selectedGroupKey: selectedGroupFor(nextVisible, current.selectedGroupKey),
    });
    notifyTodayMutation();
  },

  markTaskDoneError: (itemKey, error) => {
    const current = get();
    const nextTaskStates = {
      ...current.taskStates,
      [itemKey]: {
        state: "error",
        action: "done",
        error,
      } satisfies TaskState,
    };
    const nextStore = {
      ...current,
      taskStates: nextTaskStates,
    } as TodayStoreState;
    const prevVisible = recomputeVisibleQueue(current);
    const nextVisible = recomputeVisibleQueue(nextStore);
    set({
      taskStates: nextTaskStates,
      zeroedGroupKeys: reconcileZeroed(
        prevVisible,
        nextVisible,
        current.zeroedGroupKeys,
      ),
      selectedGroupKey: selectedGroupFor(nextVisible, current.selectedGroupKey),
    });
  },

  startTaskCreate: (tempKey, item) => {
    const current = get();
    const nextPending = {
      ...current.pendingTaskCreates,
      [tempKey]: { tempKey, item },
    };
    const nextStore = {
      ...current,
      pendingTaskCreates: nextPending,
    } as TodayStoreState;
    const prevVisible = recomputeVisibleQueue(current);
    const nextVisible = recomputeVisibleQueue(nextStore);
    set({
      pendingTaskCreates: nextPending,
      zeroedGroupKeys: reconcileZeroed(
        prevVisible,
        nextVisible,
        current.zeroedGroupKeys,
      ),
      selectedGroupKey: selectedGroupFor(nextVisible, current.selectedGroupKey),
      notice: null,
    });
  },

  finishTaskCreate: (tempKey, task) => {
    const current = get();
    if (!current.pendingTaskCreates[tempKey]) return;
    const nextPending = { ...current.pendingTaskCreates };
    delete nextPending[tempKey];
    const data =
      current.data && task
        ? queueWithTodayTask(current.data, task)
        : current.data;
    const nextStore = {
      ...current,
      data,
      pendingTaskCreates: nextPending,
    } as TodayStoreState;
    const prevVisible = recomputeVisibleQueue(current);
    const nextVisible = recomputeVisibleQueue(nextStore);
    set({
      data,
      pendingTaskCreates: nextPending,
      zeroedGroupKeys: reconcileZeroed(
        prevVisible,
        nextVisible,
        current.zeroedGroupKeys,
      ),
      selectedGroupKey: selectedGroupFor(nextVisible, current.selectedGroupKey),
    });
    if (task) notifyTodayMutation();
  },

  addTodayTask: (task) => {
    const current = get();
    if (!current.data) return;
    const data = queueWithTodayTask(current.data, task);
    if (data === current.data) return;
    const nextStore = { ...current, data } as TodayStoreState;
    const prevVisible = recomputeVisibleQueue(current);
    const nextVisible = recomputeVisibleQueue(nextStore);
    set({
      data,
      zeroedGroupKeys: reconcileZeroed(
        prevVisible,
        nextVisible,
        current.zeroedGroupKeys,
      ),
      selectedGroupKey: selectedGroupFor(nextVisible, current.selectedGroupKey),
    });
    notifyTodayMutation();
  },

  failTaskCreate: (tempKey, error) => {
    const current = get();
    if (!current.pendingTaskCreates[tempKey]) return;
    const nextPending = { ...current.pendingTaskCreates };
    delete nextPending[tempKey];
    const nextStore = {
      ...current,
      pendingTaskCreates: nextPending,
    } as TodayStoreState;
    const prevVisible = recomputeVisibleQueue(current);
    const nextVisible = recomputeVisibleQueue(nextStore);
    set({
      pendingTaskCreates: nextPending,
      error,
      zeroedGroupKeys: reconcileZeroed(
        prevVisible,
        nextVisible,
        current.zeroedGroupKeys,
      ),
      selectedGroupKey: selectedGroupFor(nextVisible, current.selectedGroupKey),
    });
  },
}));

/**
 * Refetch only the Today requests summary and merge it onto the loaded queue.
 * A failure keeps the previous summary rather than zeroing it. Nothing runs
 * before Today has loaded once: its own load fetches the summary fresh.
 */
async function refreshMarketplaceRequestsSummary(): Promise<void> {
  if (IS_PREVIEW || !marketplaceReads || !lastGetToken || !useTodayStore.getState().hasLoaded) return;
  if (requestsInFlight) return requestsInFlight;
  const getToken = lastGetToken;
  requestsInFlight = (async () => {
    try {
      const marketplaceRequests = await loadMarketplaceRequestsSummary(getToken);
      if (!marketplaceRequests) return;
      useTodayStore.setState((state) =>
        state.data
          ? { data: { ...state.data, marketplaceRequests } }
          : {},
      );
    } catch {
      // Best-effort, like the initial fetch.
    }
  })().finally(() => {
    requestsInFlight = null;
  });
  return requestsInFlight;
}

// A confirm/decline elsewhere bumps the requests store; without this the
// Today requests row stayed stale until the next full Today refresh.
useMarketplaceRequestsRefreshStore.subscribe((state, prev) => {
  if (state.version !== prev.version) void refreshMarketplaceRequestsSummary();
});

// ─────────────────────────────────────────────────────────────────────────
// Hooks/selectors.
// ─────────────────────────────────────────────────────────────────────────

export function useTodayData({ enabled = true }: { enabled?: boolean } = {}) {
  const getToken = useProToken();
  useEffect(() => {
    if (!enabled) return;
    void useTodayStore.getState().ensureLoaded(getToken);
  }, [enabled, getToken]);
  const data = useTodayStore((s) => s.data);
  const isLoading = useTodayStore((s) => s.isLoading);
  const isRefreshing = useTodayStore((s) => s.isRefreshing);
  const error = useTodayStore((s) => s.error);
  const hasLoaded = useTodayStore((s) => s.hasLoaded);
  const notice = useTodayStore((s) => s.notice);
  const selectedGroupKey = useTodayStore((s) => s.selectedGroupKey);
  const zeroedGroupKeys = useTodayStore((s) => s.zeroedGroupKeys);
  const signalStates = useTodayStore((s) => s.signalStates);
  const taskStates = useTodayStore((s) => s.taskStates);
  const pendingTaskCreates = useTodayStore((s) => s.pendingTaskCreates);

  return useMemo(
    () => ({
      data,
      isLoading,
      isRefreshing,
      error,
      hasLoaded,
      notice,
      selectedGroupKey,
      zeroedGroupKeys,
      signalStates,
      taskStates,
      pendingTaskCreates,
    }),
    [
      data,
      error,
      hasLoaded,
      isLoading,
      isRefreshing,
      notice,
      pendingTaskCreates,
      selectedGroupKey,
      signalStates,
      taskStates,
      zeroedGroupKeys,
    ],
  );
}

export function useTodaySummary() {
  const data = useTodayData();
  const queue = useVisibleTodayQueue();
  return {
    openCount: queue.openCount,
    isLoading: data.isLoading,
    isRefreshing: data.isRefreshing,
    error: data.error,
  };
}

export function useTodayQueue(options?: { enabled?: boolean }) {
  const data = useTodayData(options);
  return {
    queue: data.data,
    isLoading: data.isLoading,
    isRefreshing: data.isRefreshing,
    error: data.error,
  };
}

export function useVisibleTodayQueue() {
  const data = useTodayStore((s) => s.data);
  const signalStates = useTodayStore((s) => s.signalStates);
  const taskStates = useTodayStore((s) => s.taskStates);
  const pendingTaskCreates = useTodayStore((s) => s.pendingTaskCreates);
  return useMemo(() => {
    if (!data) return emptyQueue();
    return recomputeVisibleQueue({
      data,
      isLoading: false,
      isRefreshing: false,
      error: null,
      hasLoaded: true,
      notice: null,
      selectedGroupKey: null,
      zeroedGroupKeys: [],
      signalStates,
      taskStates,
      pendingTaskCreates,
      ensureLoaded: async () => {},
      refresh: async () => {},
      reset: () => {},
      selectGroupKey: () => {},
      clearNotice: () => {},
      beginSignalMutation: () => null,
      markSignalSuccess: () => {},
      markSignalError: () => {},
      undoSignal: () => {},
      beginTaskDoneMutation: () => null,
      markTaskDoneSuccess: () => {},
      markTaskDoneError: () => {},
      startTaskCreate: () => {},
      finishTaskCreate: () => {},
      addTodayTask: () => {},
      failTaskCreate: () => {},
    });
  }, [data, pendingTaskCreates, signalStates, taskStates]);
}

export function useSnoozedTodaySignals() {
  const data = useTodayStore((s) => s.data);
  const signalStates = useTodayStore((s) => s.signalStates);
  return useMemo(() => {
    if (!data) return [];
    const archivedSnoozed = new Set(
      Object.entries(signalStates)
        .filter(
          ([, state]) =>
            state.state === "archived" && state.action === "snoozed",
        )
        .map(([itemKey]) => itemKey),
    );
    const fromOpenQueue = data.groups
      .flatMap((group) => group.items)
      .filter(
        (item): item is TodaySignalVM =>
          item.kind === "signal" && archivedSnoozed.has(item.itemKey),
      );
    const byKey = new Map<string, TodaySignalVM>();
    for (const item of [...(data.snoozed ?? []), ...fromOpenQueue]) {
      byKey.set(item.itemKey, item);
    }
    return Array.from(byKey.values());
  }, [data, signalStates]);
}

export function todaySuggestionFromQueue(
  queue: TodayQueueVM,
): SillSuggestion | null {
  for (const group of queue.groups) {
    for (const item of group.items) {
      if (item.kind !== "signal") continue;
      return {
        clientId: item.client.id,
        text: `${item.client.name} — ${item.reason}`,
      };
    }
  }
  return null;
}

export function useTodayUi() {
  const selectedGroupKey = useTodayStore((s) => s.selectedGroupKey);
  const zeroedGroupKeys = useTodayStore((s) => s.zeroedGroupKeys);
  const signalStates = useTodayStore((s) => s.signalStates);
  const taskStates = useTodayStore((s) => s.taskStates);
  const pendingTaskCreates = useTodayStore((s) => s.pendingTaskCreates);
  const notice = useTodayStore((s) => s.notice);
  const selectGroupKey = useTodayStore((s) => s.selectGroupKey);
  const clearNotice = useTodayStore((s) => s.clearNotice);
  const beginSignalMutation = useTodayStore((s) => s.beginSignalMutation);
  const markSignalSuccess = useTodayStore((s) => s.markSignalSuccess);
  const markSignalError = useTodayStore((s) => s.markSignalError);
  const undoSignal = useTodayStore((s) => s.undoSignal);
  const beginTaskDoneMutation = useTodayStore((s) => s.beginTaskDoneMutation);
  const markTaskDoneSuccess = useTodayStore((s) => s.markTaskDoneSuccess);
  const markTaskDoneError = useTodayStore((s) => s.markTaskDoneError);
  const startTaskCreate = useTodayStore((s) => s.startTaskCreate);
  const finishTaskCreate = useTodayStore((s) => s.finishTaskCreate);
  const failTaskCreate = useTodayStore((s) => s.failTaskCreate);

  return useMemo(
    () => ({
      selectedGroupKey,
      zeroedGroupKeys,
      signalStates,
      taskStates,
      pendingTaskCreates,
      notice,
      selectGroupKey,
      clearNotice,
      beginSignalMutation,
      markSignalSuccess,
      markSignalError,
      undoSignal,
      beginTaskDoneMutation,
      markTaskDoneSuccess,
      markTaskDoneError,
      startTaskCreate,
      finishTaskCreate,
      failTaskCreate,
    }),
    [
      beginSignalMutation,
      beginTaskDoneMutation,
      clearNotice,
      failTaskCreate,
      finishTaskCreate,
      markTaskDoneError,
      markTaskDoneSuccess,
      markSignalError,
      markSignalSuccess,
      notice,
      pendingTaskCreates,
      selectGroupKey,
      selectedGroupKey,
      signalStates,
      startTaskCreate,
      taskStates,
      undoSignal,
      zeroedGroupKeys,
    ],
  );
}

export function useTodaySummaryLite() {
  return useTodaySummary();
}

// ─────────────────────────────────────────────────────────────────────────
// Network helpers.
// ─────────────────────────────────────────────────────────────────────────

export async function createTodayTask(
  getToken: GetProToken,
  body: { title: string; contact_id?: string; due_at?: string },
): Promise<TodayTaskVM> {
  if (IS_PREVIEW) {
    return {
      id: `task-${Date.now()}`,
      title: body.title,
      dueAt: body.due_at,
      dueLabel: body.due_at ? relativeDueLabel(body.due_at) : "No due date",
      createdAt: new Date().toISOString(),
      contact: body.contact_id ? fixtureContactLabel(0) : undefined,
    };
  }
  const token = (await getToken()) ?? undefined;
  const res = await postJSON<{
    id: string;
    title: string;
    due_at?: string;
    done_at?: string;
    created_at: string;
    contact?: {
      id: string;
      first_name?: string;
      last_name?: string;
      email?: string;
    };
  }>("/api/professional/tasks", body, token);
  return {
    id: res.id,
    title: res.title,
    dueAt: res.due_at,
    dueLabel: res.due_at ? relativeDueLabel(res.due_at) : "No due date",
    createdAt: res.created_at,
    doneAt: res.done_at,
    contact: res.contact
      ? {
          id: res.contact.id,
          name:
            [res.contact.first_name, res.contact.last_name]
              .filter(Boolean)
              .join(" ")
              .trim() ||
            res.contact.email?.split("@")[0] ||
            "Unnamed contact",
          email: res.contact.email,
        }
      : undefined,
  };
}

/**
 * Reconcile a task created outside Today with the existing Today queue. The
 * server remains the source of truth; this only keeps the current client view
 * in sync so an immediate "View follow-ups" action has a visible destination.
 */
export async function syncCreatedTodayTask(
  getToken: GetProToken,
  task: TodayTaskVM,
): Promise<void> {
  await useTodayStore.getState().ensureLoaded(getToken);
  useTodayStore.getState().addTodayTask(task);
}

export async function markTodayTaskDone(
  getToken: GetProToken,
  taskID: string,
): Promise<TodayTaskVM> {
  if (IS_PREVIEW) {
    return {
      id: taskID,
      title: "Task",
      dueLabel: "No due date",
      doneAt: new Date().toISOString(),
      createdAt: new Date().toISOString(),
    };
  }
  const token = (await getToken()) ?? undefined;
  const res = await patchJSON<{
    id: string;
    title: string;
    due_at?: string;
    done_at?: string;
    created_at: string;
    contact?: {
      id: string;
      first_name?: string;
      last_name?: string;
      email?: string;
    };
  }>(`/api/professional/tasks/${taskID}`, { done: true }, token);
  return {
    id: res.id,
    title: res.title,
    dueAt: res.due_at,
    dueLabel: res.due_at ? relativeDueLabel(res.due_at) : "No due date",
    createdAt: res.created_at,
    doneAt: res.done_at,
    contact: res.contact
      ? {
          id: res.contact.id,
          name:
            [res.contact.first_name, res.contact.last_name]
              .filter(Boolean)
              .join(" ")
              .trim() ||
            res.contact.email?.split("@")[0] ||
            "Unnamed contact",
          email: res.contact.email,
        }
      : undefined,
  };
}

/**
 * The one completion path for a manual follow-up. Callers provide the queue
 * item so the shared store can immediately hide it from every Today-backed
 * surface while the server remains the durable source of truth.
 */
export async function completeTodayTask(
  getToken: GetProToken,
  item: TodayTaskItemVM,
): Promise<TodayTaskVM> {
  const pendingTask = useTodayStore
    .getState()
    .beginTaskDoneMutation(item.itemKey);
  if (!pendingTask) throw new Error("Task is no longer available");

  try {
    const updated = await markTodayTaskDone(getToken, item.task.id);
    const completedTask = IS_PREVIEW
      ? { ...updated, title: item.task.title }
      : updated;
    useTodayStore.getState().markTaskDoneSuccess(item.itemKey);
    await useTodayStore
      .getState()
      .refresh(getToken, { ignoreRemovedKeys: [item.itemKey] });
    return completedTask;
  } catch (error) {
    const message = error instanceof Error ? error.message : "Could not update task";
    useTodayStore.getState().markTaskDoneError(item.itemKey, message);
    throw error;
  }
}

function relativeDueLabel(iso: string): string {
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return "No due date";
  const diffDays = Math.round((then - Date.now()) / (24 * 60 * 60 * 1000));
  if (diffDays === 0) return "Due today";
  if (diffDays === 1) return "Due tomorrow";
  if (diffDays === -1) return "Due yesterday";
  if (diffDays > 1) return `Due in ${diffDays} days`;
  return `Due ${Math.abs(diffDays)} days ago`;
}

export async function markTodaySignal(
  getToken: GetProToken,
  itemKey: string,
  action: TodayMutationKind,
  snoozeUntil?: string,
): Promise<void> {
  if (IS_PREVIEW) return;
  const token = (await getToken()) ?? undefined;
  const endpoint =
    action === "snoozed"
      ? "snooze"
      : action === "dismissed"
        ? "dismiss"
        : "done";
  const path = `/api/professional/today/items/${itemKey}/${endpoint}`;
  if (action === "snoozed") {
    await postJSON(path, { until: snoozeUntil }, token);
    return;
  }
  await postJSON(path, {}, token);
}

export async function undoTodaySignal(
  getToken: GetProToken,
  itemKey: string,
): Promise<void> {
  if (IS_PREVIEW) return;
  const token = (await getToken()) ?? undefined;
  await postJSON(`/api/professional/today/items/${itemKey}/undo`, {}, token);
}

export async function refreshTodayAfterMessage(
  getToken: GetProToken,
): Promise<void> {
  await useTodayStore.getState().refresh(getToken);
}
